import type { InfiniteData } from '@tanstack/react-query';
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { nextCursorParam } from '@/hooks/queries/pagination';
import {
  type FeeSettlementCapabilities,
  type FeeSettlementsResponse,
  getBrokerFeeSettlements,
  sortFeeSettlementsByPeriod,
  type WireFeeSettlement,
} from '@/services/api/broker-fee-settlements';
import { brokerDetailQueryKey } from './use-broker-detail';

export const brokerFeeSettlementsQueryKey = (brokerId: string) => [...brokerDetailQueryKey(brokerId), 'fee-settlements'] as const;

interface FeeSettlementsSelection {
  items: WireFeeSettlement[];
  /** from the first page — the server sends it on every page */
  capabilities: FeeSettlementCapabilities | undefined;
}

// Module-level stable select (see pagination.ts rationale). Bespoke rather than
// flattenPages because the review action also needs the caller's capabilities.
const selectFeeSettlements = (data: InfiniteData<FeeSettlementsResponse>): FeeSettlementsSelection => ({
  items: sortFeeSettlementsByPeriod(data.pages.flatMap((page) => page.items ?? [])),
  capabilities: data.pages[0]?.capabilities,
});

export const brokerFeeSettlementsInfiniteQueryOptions = (brokerId: string) =>
  infiniteQueryOptions({
    queryKey: brokerFeeSettlementsQueryKey(brokerId),
    queryFn: ({ pageParam }) => getBrokerFeeSettlements(brokerId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextCursorParam,
    select: selectFeeSettlements,
  });

export const useBrokerFeeSettlements = (brokerId: string) => useInfiniteQuery(brokerFeeSettlementsInfiniteQueryOptions(brokerId));
